/**
 * Standard reducing-balance EMI, as used by Indian banks:
 *   EMI = P * r * (1 + r)^n / ((1 + r)^n - 1)
 * where r = annual rate / 12 / 100 and n = number of monthly instalments.
 *
 * Optional moratorium (course period + grace): simple interest accrues on the
 * principal for `moratoriumMonths` and is added to the loan before repayment.
 */
export function calculateEmi({
  principal,
  annualRate,
  years,
  moratoriumMonths = 0
}) {
  const p = Number(principal);
  const months = Math.round(Number(years) * 12);
  const monthlyRate = Number(annualRate) / 12 / 100;

  if (!Number.isFinite(p) || p <= 0 || !Number.isFinite(months) || months <= 0) {
    return {
      emi: 0,
      months: 0,
      principal: 0,
      moratoriumInterest: 0,
      repayableAmount: 0,
      totalInterest: 0,
      totalPayable: 0
    };
  }

  const moratoriumInterest =
    moratoriumMonths > 0 ? p * monthlyRate * moratoriumMonths : 0;
  const repayable = p + moratoriumInterest;

  let emi;
  if (!Number.isFinite(monthlyRate) || monthlyRate <= 0) {
    emi = repayable / months;
  } else {
    const growth = (1 + monthlyRate) ** months;
    emi = (repayable * monthlyRate * growth) / (growth - 1);
  }

  const totalPayable = emi * months;
  return {
    emi,
    months,
    principal: p,
    moratoriumInterest,
    repayableAmount: repayable,
    totalInterest: totalPayable - p,
    totalPayable
  };
}

/** Round a slider maximum down to a friendly step, so it never exceeds the fee. */
export function roundDownTo(value, step) {
  return Math.floor(value / step) * step;
}
